import React from 'react';
import { Tooltip } from '@biq/apex-ui';
import { Grid, Icon, Typography } from '@mui/material';
import { themeObject, validationsMessages } from 'lib';
import * as yup from 'yup';
import InfoIcon from '@mui/icons-material/Info';
import { LabelAndValueComponent } from '../create-or-edit-user-wallet/user-wallet-from-helper';

export interface CreateAndUpdateSystemWalletFormData {
    description: string;
    type: string;
    writeUserGroup: {
        name: string;
        id: string;
    };
    accessControlUserGroup: {
        name: string;
        id: string;
    };
}

export const CreateAndUpdateSystemWalletSchema = yup.object({
    type: yup.string().required(validationsMessages.required),
    description: yup.string().required(validationsMessages.required),
    writeUserGroup: yup
        .object({
            name: yup.string(),
            id: yup.string(),
        })
        .when('type', {
            is: (value: any) =>
                value === 'TENANT_OWNER' || value === 'TENANT_LIMITED_OWNER',
            then: yup
                .object({
                    name: yup.string().required(validationsMessages.required),
                    id: yup.string().required(validationsMessages.required),
                })
                .required(validationsMessages.required),
            otherwise: yup.object({
                name: yup.string(),
                id: yup.string(),
            }),
        }),
    accessControlUserGroup: yup
        .object({
            name: yup.string().required(validationsMessages.required),
            id: yup.string().required(validationsMessages.required),
        })
        .required(validationsMessages.required),
});

export const createAndUpdateSystemWalletDefaults = {
    description: '',
    type: '',
    writeUserGroup: {
        name: '',
        id: '',
    },
    accessControlUserGroup: {
        name: '',
        id: '',
    },
};

export const createSystemWalletStyles = {
    mainContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mx: '143.5px',
    },
    labelComponentContainerStyles: {
        display: 'flex',
        flexDirection: 'row',
        mb: '4px',
    },
    labelAndRequiredStyles: {
        ...themeObject.typography.inputLabel,
        fontFamily: 'Red Hat Text',
        lineHeight: '21.17px',
        letterSpacing: '0.15px',
        color: themeObject.palette.grayScale[700],
    },
    requiredStyles: {
        color: themeObject.palette.tertiary.red[300],
    },
    tooltipTextStyles: {
        ...themeObject.typography.captions.default,
        ...themeObject.typography.captions.regular,
        color: themeObject.palette.background.default,
    },
    tooltipBoldTextStyles: {
        ...themeObject.typography.captions.default,
        ...themeObject.typography.captions.bold,
        color: themeObject.palette.background.default,
    },
    tooltipUserGroupBoldMessageStyles: {
        ...themeObject.typography.captions.medium,
    },
};

export const updateSystemWalletContainerStyles = (
    readOnly: boolean
): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: themeObject.palette.background.default,
    border: `1px solid ${themeObject.palette.grayScale[300]}`,
    width: '100%',
    borderRadius: '10px',
    marginTop: '17px',
    padding: readOnly ? '44px 228px 89px 44px' : '28px 37px 85px 33px',
});

export const typeData: {
    name: string;
    value: string;
}[] = [
    {
        name: 'Tenant Owner',
        value: 'TENANT_OWNER',
    },
    {
        name: 'Tenant Limited Owner',
        value: 'TENANT_LIMITED_OWNER',
    },
    {
        name: 'Custodian Owner',
        value: 'CUSTODIAN_OWNER',
    },
    {
        name: 'Custodian Limited Owner',
        value: 'CUSTODIAN_LIMITED_OWNER',
    },
];

export const tooltipMessages = (
    field: 'type' | 'accessControlUserGroup' | 'writeUserGroup'
): any => {
    if (field === 'type') {
        return (
            <Typography
                component={'ul'}
                sx={createSystemWalletStyles.tooltipTextStyles}
            >
                <Typography component={'li'}>
                    <Typography
                        component={'span'}
                        sx={createSystemWalletStyles.tooltipBoldTextStyles}
                    >
                        CUSTODIAN_OWNER:
                    </Typography>{' '}
                    will inject this system wallet as co-owner of ALL <br />
                    assets. They can perform CRUD and transfer actions on all
                    assets.
                </Typography>
                <Typography component={'li'}>
                    <Typography
                        component={'span'}
                        sx={createSystemWalletStyles.tooltipBoldTextStyles}
                    >
                        CUSTODIAN_LIMITED_OWNER:
                    </Typography>{' '}
                    will inject this system wallet as a co-
                    <br />
                    limited owner of ALL assets. They can read and consent all
                    assets.
                </Typography>
                <Typography component={'li'}>
                    <Typography
                        component={'span'}
                        sx={createSystemWalletStyles.tooltipBoldTextStyles}
                    >
                        TENANT_OWNER:
                    </Typography>{' '}
                    will inject this system wallet as a co-owner on any
                    <br />
                    asset created by user wallets in the ”writeUserGroup” user
                    group. They can <br /> perform CRUD and transfer actions on
                    assets created by users in the <br />
                    “writeUserGroup”.
                </Typography>
                <Typography component={'li'}>
                    <Typography
                        component={'span'}
                        sx={createSystemWalletStyles.tooltipBoldTextStyles}
                    >
                        TENANT_LIMITED_OWNER:
                    </Typography>{' '}
                    will inject this system wallet as a co-limited <br /> owner
                    on any asset created by user wallets in the “writeUserGroup”
                    user <br /> group. They can read and consent assets created
                    by users in the <br />
                    “writeUserGroup.”
                </Typography>
            </Typography>
        );
    } else if (field === 'accessControlUserGroup') {
        return (
            <Typography
                sx={{
                    ...createSystemWalletStyles.tooltipTextStyles,
                    ...createSystemWalletStyles.tooltipUserGroupBoldMessageStyles,
                }}
            >
                Users within the{' '}
                <Typography
                    component={'span'}
                    sx={createSystemWalletStyles.tooltipBoldTextStyles}
                >
                    Access Control <br /> Group
                </Typography>{' '}
                can elevate their access to <br /> this system wallet.
            </Typography>
        );
    } else if (field === 'writeUserGroup') {
        return (
            <Typography
                sx={{
                    ...createSystemWalletStyles.tooltipTextStyles,
                    ...createSystemWalletStyles.tooltipUserGroupBoldMessageStyles,
                }}
            >
                Users in the{' '}
                <Typography
                    component={'span'}
                    sx={createSystemWalletStyles.tooltipBoldTextStyles}
                >
                    Write Access Group
                </Typography>
                <br />
                will have this system wallet ID added <br /> to the ownership
                header for the <br /> given SDO.
            </Typography>
        );
    }
};

export const LabelComponent = ({
    label,
    tooltipMessage,
}: {
    label: string;
    tooltipMessage: string;
}): React.JSX.Element => {
    const popperStyles =
        label === 'Type'
            ? {
                  sx: {
                      '& .MuiTooltip-tooltip': {
                          maxWidth: '600px',
                      },
                  },
              }
            : {};

    return (
        <Grid sx={createSystemWalletStyles.labelComponentContainerStyles}>
            <Grid>
                <Typography
                    sx={createSystemWalletStyles.labelAndRequiredStyles}
                >
                    {label}
                    <Typography
                        component={'span'}
                        sx={createSystemWalletStyles.requiredStyles}
                    >
                        *
                    </Typography>
                </Typography>
            </Grid>
            <Tooltip
                title={tooltipMessage}
                arrow
                placement={'top'}
                slotProps={{
                    popper: popperStyles,
                }}
            >
                <Icon
                    fontSize='inherit'
                    color='primary'
                    sx={{
                        '& .MuiSvgIcon-root': {
                            height: '14px',
                            width: '14px',
                            mb: '3px',
                        },
                    }}
                >
                    <InfoIcon />
                </Icon>
            </Tooltip>
        </Grid>
    );
};

export const ReadOnlyFields = ({
    getValues,
    isWriteUserGroupFieldEnabled,
}: {
    getValues: any;
    isWriteUserGroupFieldEnabled: boolean;
}): React.JSX.Element => (
    <>
        <LabelAndValueComponent label={'Type'} value={getValues('type')} />
        <LabelAndValueComponent
            label={'Access Control User Group'}
            value={
                getValues('accessControlUserGroup').name
                    ? getValues('accessControlUserGroup').name
                    : '- -'
            }
        />
        {isWriteUserGroupFieldEnabled && (
            <LabelAndValueComponent
                label={'Write User Group'}
                value={
                    getValues('writeUserGroup').name
                        ? getValues('writeUserGroup').name
                        : '- -'
                }
            />
        )}
        <LabelAndValueComponent
            label={'Description'}
            value={getValues('description') ? getValues('description') : '- -'}
        />
    </>
);
